import { useEffect } from "react";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useGetWorkbenchCollaborators } from "@vega-api/generated/auth-workbench-v1/auth-workbench-v1";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import { useSingleCollaboratorRolesManager } from "../hooks/useSingleCollaboratorRolesManager";

import { CollaboratorsSideDrawerHeaderV2 } from "./CollaboratorsSideDrawerHeaderV2";
import { CollaboratorsSideDrawerRolesV2 } from "./CollaboratorsSideDrawerRolesV2";
import { SaveRoleChangesModal } from "./SaveRoleChangesModal";

type CollaboratorsSideDrawerV2ContentProps = {
  isSideDrawerOpen: boolean;
  selectedCollaborator: CollaboratorDetails | null;
};

export const CollaboratorsSideDrawerV2Content = ({
  isSideDrawerOpen,
  selectedCollaborator: initialCollaborator,
}: CollaboratorsSideDrawerV2ContentProps) => {
  const { data: collaboratorsData } = useGetWorkbenchCollaborators();
  const selectedCollaborator =
    collaboratorsData?.data?.find(
      (collaborator) => collaborator.id === initialCollaborator?.id,
    ) ?? initialCollaborator;
  const { isOpen: isEditing, setIsOpen: setIsEditing } = useBoolean(false);
  const { isOpen: isConfirmModalOpen, setIsOpen: setIsConfirmModalOpen } =
    useBoolean(false);
  const showToast = useToast();

  const manager = useSingleCollaboratorRolesManager({
    selectedCollaborator,
    isEditing,
  });

  useEffect(() => {
    if (!isSideDrawerOpen) {
      setIsEditing(false);
      manager.resetChanges();
    }
  }, [isSideDrawerOpen]);

  const handleSave = () => {
    if (manager.hasChanges) {
      setIsConfirmModalOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmChanges = () => {
    manager.save({
      onSuccess: () => {
        showToast({
          title: m.settings_collaborators_roleChangesSaved(),
          variant: "success",
        });
        manager.resetChanges();
        setIsEditing(false);
      },
      onError: (error) => {
        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.settings_collaborators_roleChangesFailed(),
        });
        showToast({ title, description, variant: "destructive" });
      },
    });
  };

  return (
    <>
      <div className="bg-background/secondary">
        <div className="px-6 pb-6">
          <CollaboratorsSideDrawerHeaderV2
            isEditing={isEditing}
            isSaveDisabled={manager.hasInvalidEntries}
            onCancel={() => {
              manager.resetChanges();
              setIsEditing(false);
            }}
            onManageRoles={() => {
              manager.resetChanges();
              setIsEditing(true);
            }}
            onSave={handleSave}
          />
        </div>
      </div>
      <div className="flex flex-col w-full h-full px-6 pb-8 gap-6">
        {selectedCollaborator ? (
          <div className="mt-5">
            <CollaboratorsSideDrawerRolesV2
              isEditing={isEditing}
              manager={manager}
            />
          </div>
        ) : null}
      </div>
      <SaveRoleChangesModal
        editedPermissions={manager.editedPermissions}
        getRoleNameById={manager.getRoleNameById}
        onConfirm={handleConfirmChanges}
        onDiscard={() => {
          manager.resetChanges();
          setIsEditing(false);
        }}
        open={isConfirmModalOpen}
        pendingCreations={manager.pendingCreations}
        permissionsToDelete={manager.getPermissionsMarkedForDeletion()}
        setOpen={setIsConfirmModalOpen}
      />
    </>
  );
};
